import type { OpenClawApp } from "../app.ts";
import { resolveAgentIdForSession } from "../chat/resolve-session-agent.ts";
import { setSessionTabRunning } from "../chat/session-tab-activity.ts";
import type { SelfImprovementJobRecord, SelfImprovementJobStatus } from "../types.ts";
import type { ChatSubagentCard } from "../ui-types.ts";
import { injectChatMessage, loadChatHistory } from "./chat.ts";
import {
  formatFleetSelfImprovementError,
  getFleetSelfImprovementStatus,
  resolveFleetSelfImprovementWorkspace,
  type FleetSelfImprovementStartResult,
} from "./fleet-self-improvement.ts";
import { loadFleetSessionFiles } from "./fleet-session-files.ts";
import { createPendingSubagentCard } from "./subagents.ts";

const POLL_INTERVAL_MS = 5_000;
const POLL_MAX_ATTEMPTS = 240;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isTerminalSelfImprovementStatus(status: SelfImprovementJobStatus): boolean {
  return status === "shipped" || status === "failed";
}

export function buildSelfImprovementStartedAck(res: FleetSelfImprovementStartResult): string {
  const issue = res.job.youtrackIssueReadable ?? res.job.youtrackIssueId ?? "YouTrack task";
  const repo = res.job.repoWorkspaceDir?.trim();
  return [
    `**Self improvement started:** ${issue}`,
    "",
    `Worker tab: \`${res.childSessionKey}\` (run \`${res.runId}\`).`,
    "Live output appears in the worker chat tab. Use **Subagent activity** for a compact status view.",
    repo ? `Worker repo: \`${repo}\`.` : "",
    "Marked **shipped** only after git confirms edits in that checkout.",
    res.warning ? `\n\n_${res.warning}_` : "",
  ]
    .filter(Boolean)
    .join("\n");
}

export function buildSelfImprovementFinishedAck(job: SelfImprovementJobRecord): string {
  const issue = job.youtrackIssueReadable ?? job.youtrackIssueId ?? "task";
  if (job.status === "shipped") {
    const paths = job.changedPaths?.length
      ? job.changedPaths.map((p) => `- \`${p}\``).join("\n")
      : "- (paths not recorded)";
    return [
      `**Self improvement — repo updated:** ${issue}`,
      "",
      "Changed files in your checkout (push/YouTrack may still be in progress):",
      paths,
      "",
      "Run `pnpm ui:build` (or Developer → build) if you changed Control UI assets.",
    ].join("\n");
  }
  const err = job.error?.trim() || "Worker did not complete successfully.";
  return [
    `**Self improvement failed:** ${issue}`,
    "",
    err,
    "",
    "Open the worker tab and check **Local LLM** (use `ollama/qwen3-coder:latest` or set `JAISIU_SELF_IMPROVEMENT_MODEL`).",
  ].join("\n");
}

export function registerSelfImprovementSubagentCard(
  app: OpenClawApp,
  params: {
    goal: string;
    res: FleetSelfImprovementStartResult;
    parentSessionKey: string;
  },
): ChatSubagentCard {
  const issue = params.res.job.youtrackIssueReadable ?? params.res.job.youtrackIssueId ?? "task";
  const label = `self-improve:${issue}`;
  const card: ChatSubagentCard = {
    ...createPendingSubagentCard(params.goal, params.res.job.workerAgentId, label),
    status: "running",
    runId: params.res.runId,
    childSessionKey: params.res.childSessionKey,
  };
  const existing = app.chatSubagents ?? [];
  const withoutDup = existing.filter((c) => c.childSessionKey !== params.res.childSessionKey);
  app.chatSubagents = [...withoutDup, card];
  app.sessionTabActivity = setSessionTabRunning(
    app.sessionTabActivity,
    params.res.childSessionKey,
    true,
  );
  return card;
}

function updateSelfImprovementSubagentCard(
  app: OpenClawApp,
  params: { childSessionKey: string; job: SelfImprovementJobRecord },
): void {
  const { childSessionKey, job } = params;
  const status: ChatSubagentCard["status"] =
    job.status === "shipped" ? "done" : job.status === "failed" ? "error" : "running";
  app.chatSubagents = (app.chatSubagents ?? []).map((card) => {
    if (card.childSessionKey !== childSessionKey) {
      return card;
    }
    return {
      ...card,
      status,
      error: job.status === "failed" ? job.error?.trim() || "Self improvement failed" : undefined,
      phase: job.status === "shipped" ? "shipped" : job.status === "failed" ? "failed" : card.phase,
      changedPaths: job.changedPaths?.length ? job.changedPaths : card.changedPaths,
      updatedAt: Date.now(),
    };
  });
  if (job.status === "shipped" || job.status === "failed") {
    app.sessionTabActivity = setSessionTabRunning(app.sessionTabActivity, childSessionKey, false);
  }
}

async function finalizeSelfImprovementJob(
  app: OpenClawApp,
  host: Parameters<typeof injectChatMessage>[0],
  params: {
    job: SelfImprovementJobRecord;
    childSessionKey: string;
    parentSessionKey: string;
  },
): Promise<void> {
  updateSelfImprovementSubagentCard(app, {
    childSessionKey: params.childSessionKey,
    job: params.job,
  });

  const parentKey = params.parentSessionKey.trim();
  await injectChatMessage(host, buildSelfImprovementFinishedAck(params.job), "self-improvement", {
    sessionKey: parentKey,
  });
  if (host.sessionKey.trim() === parentKey) {
    void loadChatHistory(host);
  }

  if (params.job.status === "failed") {
    app.lastError = params.job.error?.trim() || "Self improvement finished without repo changes.";
  }
}

export async function pollSelfImprovementJobUntilTerminal(
  app: OpenClawApp,
  host: Parameters<typeof injectChatMessage>[0],
  params: {
    agentId: string;
    jobId: string;
    childSessionKey: string;
    parentSessionKey: string;
  },
): Promise<void> {
  if (!app.client) {
    return;
  }
  for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt += 1) {
    await sleep(POLL_INTERVAL_MS);
    if (!app.client) {
      return;
    }
    try {
      const { job } = await getFleetSelfImprovementStatus(app.client, {
        agentId: params.agentId,
        jobId: params.jobId,
      });
      if (!isTerminalSelfImprovementStatus(job.status)) {
        continue;
      }
      await finalizeSelfImprovementJob(app, host, {
        job,
        childSessionKey: params.childSessionKey,
        parentSessionKey: params.parentSessionKey,
      });
      return;
    } catch {
      // keep polling until timeout
    }
  }
}

/** Toggle Self improvement: preview worker repo path when on; restore session paths when off. */
export async function handleChatSendSelfImprovementChange(
  app: OpenClawApp,
  next: boolean,
): Promise<void> {
  // Pillar F — write through the per-session mirror so the toggle does
  // not leak across sessions. The helper also flips the @state() and
  // writes the legacy `chatSendSelfImprovement` global default.
  app.writeChatSendOption("selfImprovement", next);
  if (next) {
    app.writeChatSendOption("orchestrateWithAll", false);
    app.chatSelfImprovementRepoWorkspace = null;
    if (!app.client || !app.connected) {
      return;
    }
    const agentId = resolveAgentIdForSession(app.sessionKey);
    try {
      const res = await resolveFleetSelfImprovementWorkspace(app.client, {
        agentId,
        sessionKey: app.sessionKey,
      });
      if (res.selfImprovementEnabled === false) {
        app.lastError =
          "YouTrack ticket creation and Fleet self-improvement are disabled in the OTGS workspace.";
        app.writeChatSendOption("selfImprovement", false);
        app.chatSelfImprovementRepoWorkspace = null;
        return;
      }
      if (res.isJaisiuMonorepo === false) {
        app.lastError =
          res.workerWorkspaceWarning?.trim() ||
          "Self improvement requires agents.defaults.repoRoot pointing at your Jaisiu monorepo.";
        app.writeChatSendOption("selfImprovement", false);
        app.chatSelfImprovementRepoWorkspace = null;
        return;
      }
      app.chatSelfImprovementRepoWorkspace = res.workerWorkspaceDir?.trim() || null;
    } catch (err) {
      app.lastError = formatFleetSelfImprovementError(err);
      app.writeChatSendOption("selfImprovement", false);
    }
    return;
  }

  app.chatSelfImprovementRepoWorkspace = null;
  app.chatWorkspaceLoading = false;
  app.chatWorkspaceAgentId = null;
  await loadFleetSessionFiles(app as unknown as Parameters<typeof loadFleetSessionFiles>[0]);
}

export async function applyFleetSelfImprovementStarted(
  app: OpenClawApp,
  host: Parameters<typeof injectChatMessage>[0],
  params: {
    goal: string;
    res: FleetSelfImprovementStartResult;
  },
): Promise<void> {
  const parentSessionKey = host.sessionKey.trim();
  const ack = buildSelfImprovementStartedAck(params.res);

  await injectChatMessage(host, ack, "self-improvement");
  registerSelfImprovementSubagentCard(app, {
    goal: params.goal,
    res: params.res,
    parentSessionKey,
  });

  app.chatSelfImprovementRepoWorkspace = null;
  app.chatWorkspaceLoading = false;
  app.chatWorkspaceAgentId = null;
  void app.refreshFleetProjects();
  void app.refreshChatWorkspace();

  app.applySettings({
    ...app.settings,
    fleetChatMainTab: "chat",
  });

  // I5 — stay on parent unless the operator opted into auto-open (pinned
  // false in storage migration, matching onSubagentSpawned).
  if (app.settings.chatAutoOpenSubagentTabs) {
    app.openSubagentChatTab({
      childKey: params.res.childSessionKey,
      parentKey: parentSessionKey,
      label: `self-improve:${params.res.job.youtrackIssueReadable ?? params.res.job.youtrackIssueId ?? "task"}`,
    });
    app.activateChatSession(params.res.childSessionKey);
  }

  const agentId = resolveAgentIdForSession(parentSessionKey);
  void pollSelfImprovementJobUntilTerminal(app, host, {
    agentId,
    jobId: params.res.job.id,
    childSessionKey: params.res.childSessionKey,
    parentSessionKey,
  });
}
